import { useState } from 'react';
import { Button, Stack, Switch, TextField, Typography } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { toast } from 'react-toastify';
import formatDate from '../../../../utils/formatDate';
import { convertHM, formatHours } from '../../../../utils/timeCalculation';
import CustomPopover from '../../../../components/custom-popover';
import { editProjectMemberStatus } from '../../../../services/projectService';

const boldHeader = (color) => (table) => <b style={{ color }}>{table?.column?.columnDef?.header}</b>;

export const EMPLOYEE_TIMELINE_TABLE_COLUMNS = [
  {
    accessorKey: 'userName',
    id: 'userName',
    header: 'User',
    Header: boldHeader('#454F5B'),
    enableColumnFilter: false,
    size: 30,
  },
  {
    accessorKey: 'project.projectName',
    id: 'projectName',
    header: 'Project',
    Header: boldHeader('#454F5B'),
    enableColumnFilter: false,
    size: 30,
  },
  {
    accessorKey: 'startDate',
    header: 'Start Date',
    id: 'startDate',
    Header: boldHeader('#454F5B'),
    Cell: ({ row }) => (row.original.startDate ? formatDate(row.original.startDate) : 'N/A'),
    enableSorting: false,
    enableColumnFilter: false,
    size: 30,
  },
  {
    accessorKey: 'projectMember.userHours',
    header: 'Assigned Hours',
    id: 'projectMember.userHours',
    Header: boldHeader('#454F5B'),
    Cell: ({ row }) =>
      row.original.projectMember.userHours ? formatHours(row.original.projectMember.userHours) : 'N/A',
    enableColumnFilter: false,
    enableSorting: false,
    size: 30,
  },
  {
    accessorKey: 'totalConsumedTime',
    header: 'Consumed Hours',
    id: 'totalConsumedTime',
    Header: boldHeader('#454F5B'),
    Cell: ({ row }) => (row.original.totalConsumedTime ? convertHM(row.original.totalConsumedTime) : 'N/A'),
    enableColumnFilter: false,
    enableSorting: false,
    size: 30,
  },
  {
    accessorKey: 'isActive',
    header: 'Status',
    id: 'isActive',
    Header: boldHeader('#454F5B'),
    Cell: ({ row }) => {
      const [anchorEl, setAnchorEl] = useState(null);
      const [endDate, setEndDate] = useState(new Date());
      const [isPopoverOpen, setPopoverOpen] = useState(false);

      const handleSwitchChange = (event) => {
        setPopoverOpen(true);
        setAnchorEl(event.currentTarget);
      };

      const handleDateChange = (newDate) => {
        setEndDate(newDate);
      };

      const handleEndDateSubmit = async () => {
        // Reset hours, minutes, and seconds to 0
        const formattedEndDate = new Date(endDate);
        formattedEndDate.setHours(0, 0, 0, 0); // Set time to 00:00:00

        const payLoad = {
          endDate: formattedEndDate,
          projectId: row.original.projectId,
          startDate: new Date(row.original.startDate),
          isActive: !row.original.isActive,
          userId: row.original.userId,
        };

        try {
          const response = await editProjectMemberStatus(payLoad);
          toast.success(response?.data?.message);
        } catch (error) {
          toast.error('An error occurred while Updating the project member. Please try again.');
        }
        setPopoverOpen(false); // Close popover after submitting end date
      };

      return (
        <>
          <Switch checked={row.original.isActive} onChange={handleSwitchChange} color="primary" />
          <CustomPopover
            open={isPopoverOpen ? anchorEl : null}
            onClose={() => setPopoverOpen(false)}
            anchorEl={anchorEl}
            arrow="top-right"
          >
            <Stack direction="column" spacing={2} sx={{ padding: '1rem' }}>
              <Typography variant="h6">{`${`Select ${
                row.original.isActive ? 'Deactivation' : 'Activation'
              } End Date`}`}</Typography>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  value={endDate}
                  onChange={handleDateChange}
                  disablePast
                  inputFormat="DD MMM YYYY"
                  renderInput={(params) => <TextField {...params} />}
                />
              </LocalizationProvider>
              <Button variant="contained" color="primary" onClick={handleEndDateSubmit} style={{ marginTop: '1rem' }}>
                Submit
              </Button>
            </Stack>
          </CustomPopover>
        </>
      );
    },
    enableSorting: false,
    enableColumnFilter: false,
    size: 30,
  },
  // {
  //   accessorKey: 'actions',
  //   header: 'Actions',
  //   id: 'actions',
  //   size: 10,
  //   enableColumnFilter: false,
  //   enableSorting: false,
  //   Header: boldHeader('#454F5B'),
  // },
];

export const EMPLOYEE_TIMELINE_TABLE_DEFAULT_PROPS = {
  // positionActionsColumn: 'last',
  enableFullScreenToggle: false,
  enableColumnActions: false,
  manualPagination: true,
  manualFiltering: true,
  manualSorting: true,
  muiTablePaperProps: {
    sx: {
      color: 'rgb(71 85 105 / var(--tw-text-opacity))',
    },
  },
  muiTopToolbarProps: {
    sx: {
      backgroundColor: '#fff',
    },
  },
  muiTableHeadRowProps: {
    sx: {
      '--tw-bg-opacity': 1,
      backgroundColor: 'rgb(226 232 240 / var(--tw-bg-opacity))',
      fontWeight: '900',
    },
  },
  muiTableHeadCellFilterTextFieldProps: {
    sx: {
      m: '0.5rem 0',
      borderColor: 'white',
      '& input': {
        borderColor: 'white',
        '&::placeholder': {
          color: 'white',
        },
      },
      '& .MuiInput-root:before, &.MuiInputBase-root:before': {
        borderBottomColor: 'white',
        color: 'white',
      },
      '& .MuiInputBase-input, &.MuiButtonBase-root, &.MuiIconButton-root': {
        textTransform: 'capitalize',
        color: 'white',
      },
    },
  },
  muiTableHeadCellProps: {
    sx: {
      backgroundColor: '#F9FAFB',
      margin: 'auto',
      // color: 'white',
      fontWeight: '800',
      fontSize: '.75rem',
      lineHeight: '2rem',
      textTransform: 'uppercase',
      '--tw-text-opacity': 1,
      // color: 'white',
      borderColor: '#F9FAFB',
      // paddingTop: '1.25rem',
      paddingRight: '1.5rem',
      paddingBottom: '1.25rem',
      paddingLeft: '1.5rem',
      ' & .Mui-TableHeadCell-Content': {
        position: 'relative',
        top: '5px',
      },
    },
  },
  muiTableBodyCellProps: {
    sx: {
      '--tw-border-opacity': 1,
      borderColor: 'rgb(241 245 249 / var(--tw-border-opacity))',
      '--tw-text-opacity': 1,
      paddingLeft: '1.5rem',
    },
  },
};
